import { ReportContext, ReportParameters } from '../internal/ReportContext';
import { ExportPDFOptions } from '../utils/printUtils';
import { ReportController } from '@sas-dvr/ltjs-bird';
import { ReplaySubject } from 'rxjs';
import { ObjectHandle } from './ObjectHandle';
import { assertValidContext, waitForReportSessionReady } from './handleUtils';
import { ReportSession } from '@sas-dvr/ltjs-vav';

/**
 * @public @sealed
 */
export class ReportHandle {
  /**
   * @internal
   */
  private _context: ReportContext | null;
  /**
   * @internal
   */
  private _objectHandles: Map<string, ObjectHandle> = new Map();
  /**
   * @internal
   */
  private _destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  /**
   * @internal
   */
  private _eventListeners: Map<
    string,
    Set<{
      callback: (event: { type: string; target: ReportHandle }) => void;
      handle: { detach: () => void };
    }>
  > = new Map();

  /**
   * @internal
   */
  constructor(context: ReportContext) {
    this._context = context;
  }

  get readyState(): 'loading' | 'contentLoading' | 'complete' | 'error' {
    if (!this._context) {
      return 'error';
    }
    return this._context.getReadyState();
  }

  async getObjectHandle(objectName: string): Promise<ObjectHandle> {
    assertValidContext(this._context);
    const reportController = await this._getReportController();
    const objectController = reportController.getVisualElementControllerForVisualElementName(
      objectName
    );
    if (!objectController) {
      throw new Error(`The object ${objectName} could not be found in the report`);
    }
    let objectHandle = this._objectHandles.get(objectName);
    if (!objectHandle) {
      objectHandle = new ObjectHandle(this._context, objectName);
      this._objectHandles.set(objectName, objectHandle);
    }
    return objectHandle;
  }

  setReportParameters(val: undefined | ReportParameters): void {
    assertValidContext(this._context);
    this._context.setParameters(val);
  }

  updateReportParameters(val: ReportParameters): void {
    assertValidContext(this._context);
    this._context.updateParameters(val);
  }

  async exportPDF(options?: ExportPDFOptions): Promise<string> {
    assertValidContext(this._context);
    // The report may not have loaded yet, so wait for the ReportSession to be
    // available before calling exportPDF.
    await waitForReportSessionReady(this._context, this._destroyed$);
    return this._context.exportPDF(options);
  }

  /**
   * Waits for the report session to intitialize, then returns its report controller.
   * For use in Office 365 only.
   * @internal
   */
  async _getReportController(): Promise<ReportController> {
    assertValidContext(this._context);
    const reportSession = await waitForReportSessionReady(this._context, this._destroyed$);
    const reportController = reportSession.getReportController();
    if (!reportController) {
      throw new Error('Report controller could not be created.');
    }
    return reportController;
  }

  /**
   * Waits for the report session to intitialize, then returns it.
   * For use in Office 365 only.
   * @internal
   */
  async _getReportSession(): Promise<ReportSession> {
    assertValidContext(this._context);
    return waitForReportSessionReady(this._context, this._destroyed$);
  }

  /**
   * @internal
   */
  async saveUserStateXML() {
    await this._getReportController();
    assertValidContext(this._context);
    return this._context.saveUserStateXML();
  }

  refreshData(): void {
    assertValidContext(this._context);
    if (this._context.getSessionStatusNotifier().getValue() !== 'ready') {
      // no-op if the report has not loaded or has errored
      return;
    }
    // Portable does not provide a way to know when all elements have been refreshed,
    // but so far no use cases need to know when the operation is complete.
    this._context.getReportController()?.refreshData();
  }

  reloadReport(ignoreUserState?: true) {
    assertValidContext(this._context);
    this._context.reloadReport(ignoreUserState);
  }

  addEventListener(
    type: 'readyStateChanged',
    callback: (event: { type: string; target: ReportHandle }) => void
  ) {
    assertValidContext(this._context);
    const events = this._eventListeners.get(type) || new Set();
    for (const event of events) {
      if (event.callback === callback) {
        return;
      }
    }
    const eventDetachHandle = this._context.subscribeEvent(type, {
      type: 'report',
      callback,
      reportHandle: this,
    });
    if (eventDetachHandle) {
      this._eventListeners.set(type, events.add({ callback, handle: eventDetachHandle }));
    }
  }

  removeEventListener(
    type: 'readyStateChanged',
    callback: (event: { type: string; target: ReportHandle }) => void
  ) {
    const events = this._eventListeners.get(type);
    if (!events) {
      return;
    }
    for (const event of events) {
      if (event.callback === callback) {
        event.handle.detach();
        events.delete(event);
        break;
      }
    }
  }

  /**
   * Prevents the handle from being used after the context goes out of scope
   * @internal
   */
  destroy() {
    for (const eventListener of this._eventListeners.values()) {
      for (const eventInfo of eventListener.values()) {
        eventInfo.handle.detach();
      }
    }
    this._eventListeners.clear();
    for (const objectHandle of this._objectHandles.values()) {
      objectHandle.destroy();
    }
    this._objectHandles.clear();
    this._context = null;
    this._destroyed$.next(true);
  }
}
